import { isAxiosError } from "axios";
import api from "../../../lib/axios";
import { BaseUserResponse } from "../../auth/data";

export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
  new_password_confirmation: string;
}

export interface UpdateProfileRequest {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  profileImageFile: File | null;
  language?: string;
  gender?: string;
}

type ApiRecord = Record<string, unknown>;

interface UpdateProfileApiResponse {
  success?: boolean;
  data?: unknown;
  user?: unknown;
  message?: string;
  user_message?: string;
}

interface CurrentUserProfileApiResponse {
  success: boolean;
  data?: Partial<BaseUserResponse>;
  message?: string;
  user_message?: string;
}

interface ChangePasswordApiResponse {
  success: boolean;
  data?: unknown;
  message?: string;
  user_message?: string;
}

const isRecord = (value: unknown): value is ApiRecord =>
  typeof value === "object" && value !== null;

const getNestedUser = (value: unknown): Partial<BaseUserResponse> | null => {
  if (!isRecord(value)) {
    return null;
  }

  if (isRecord(value.user)) {
    return value.user as Partial<BaseUserResponse>;
  }

  return value as Partial<BaseUserResponse>;
};

const getResponseMessage = (value: unknown, fallback: string): string => {
  if (!isRecord(value)) {
    return fallback;
  }

  return typeof value.user_message === "string"
    ? value.user_message
    : typeof value.message === "string"
      ? value.message
      : fallback;
};

export const getProfileErrorMessage = (error: unknown, fallback: string): string => {
  if (isAxiosError(error)) {
    return getResponseMessage(error.response?.data, error.message || fallback);
  }

  return error instanceof Error ? error.message : fallback;
};

export async function getCurrentUserProfile(
  signal?: AbortSignal,
): Promise<Partial<BaseUserResponse>> {
  const response = await api.get<CurrentUserProfileApiResponse>("v1/me/profile", {
    signal,
  });
  const result = response.data;

  if (!result.success || !result.data) {
    throw new Error(getResponseMessage(result, "Failed to retrieve user profile"));
  }

  return result.data;
}

export async function updateProfile(
  request: UpdateProfileRequest,
): Promise<Partial<BaseUserResponse>> {
  const profileData = new FormData();
  profileData.append("first_name", request.firstName);
  profileData.append("last_name", request.lastName);
  profileData.append("phone_number", request.phoneNumber);
  if (request.language) {
    profileData.append("language", request.language);
  }

  if (request.gender) {
    profileData.append("gender", request.gender);
  }

  if (request.profileImageFile) {
    profileData.append("avatar", request.profileImageFile);
  }

  const response = await api.put<UpdateProfileApiResponse>(
    "v1/me/update-profile-admin",
    profileData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  const result = response.data;
  const user = getNestedUser(result.data) ?? getNestedUser(result.user);

  if (!result.success || !user) {
    throw new Error(getResponseMessage(result, "Profile update failed"));
  }

  return user;
}

export async function changePassword(request: ChangePasswordRequest): Promise<void> {
  const response = await api.post<ChangePasswordApiResponse>("v1/me/change-password", {
    data: request,
  });

  if (!response.data.success) {
    throw new Error(getResponseMessage(response.data, "Failed to change password"));
  }
}
